/**
 * Card Component
 * 
 * A container component for grouping related content.
 * 
 * @module components/ui/Card
 */

import { forwardRef, type HTMLAttributes } from 'react';
import { cn } from '@/lib/utils';

// =============================================================================
// Card
// =============================================================================

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /** Remove padding */
  noPadding?: boolean;
}

export const Card = forwardRef<HTMLDivElement, CardProps>(
  ({ className, noPadding, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          'bg-white rounded-xl border border-gray-200 shadow-soft',
          !noPadding && 'p-6',
          className
        )}
        {...props}
      >
        {children}
      </div>
    );
  }
);

Card.displayName = 'Card';

// =============================================================================
// Card Header
// =============================================================================

export interface CardHeaderProps extends HTMLAttributes<HTMLDivElement> {
  /** Card title */
  title: string;
  /** Card description */
  description?: string;
  /** Action element (e.g., button) */
  action?: React.ReactNode;
}

export const CardHeader = forwardRef<HTMLDivElement, CardHeaderProps>(
  ({ className, title, description, action, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn('flex items-start justify-between gap-4 mb-4', className)}
        {...props}
      >
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          {description && (
            <p className="text-sm text-gray-500 mt-0.5">{description}</p>
          )}
        </div>
        {action && <div className="flex-shrink-0">{action}</div>}
      </div>
    );
  }
);

CardHeader.displayName = 'CardHeader';

// =============================================================================
// Stat Card (for dashboard metrics)
// =============================================================================

export interface StatCardProps extends HTMLAttributes<HTMLDivElement> {
  /** Stat label */
  label: string;
  /** Stat value */
  value: string | number;
  /** Optional icon */
  icon?: React.ReactNode;
  /** Change indicator (e.g., "+12%") */
  change?: string;
  /** Whether change is positive */
  changePositive?: boolean;
}

export const StatCard = forwardRef<HTMLDivElement, StatCardProps>(
  ({ className, label, value, icon, change, changePositive, ...props }, ref) => {
    return (
      <Card ref={ref} className={cn('', className)} {...props}>
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm font-medium text-gray-500">{label}</p>
            <p className="text-2xl font-semibold text-gray-900 mt-1">{value}</p>
            {change && (
              <p
                className={cn(
                  'text-sm mt-1',
                  changePositive ? 'text-success-600' : 'text-error-600'
                )}
              >
                {change}
              </p>
            )}
          </div>
          {icon && (
            <div className="p-2 bg-gray-100 rounded-lg text-gray-600">
              {icon}
            </div>
          )}
        </div>
      </Card>
    );
  }
);

StatCard.displayName = 'StatCard';
